import { useId, useState } from 'react';
import { cn } from '@/lib/utils';
import './workmate-loader.css';
import { WorkmateTaskArt } from './workmate-task-art';

export type WorkmateScene = 'welcome' | 'shifts' | 'invoices' | 'juggling' | 'team' | 'approvals';

const scenes = {
  welcome: {
    crop: '25 110 460 590',
    part: 'M365 443L387 405L394 354L420 339L458 345L485 384L478 445L445 489L404 521L366 517Z',
    pivot: '370px 491px',
    label: 'Loading your workspace…',
  },
  shifts: {
    crop: '550 150 495 550',
    part: 'M826 481L842 428L1029 427L1061 675L822 675Z',
    pivot: '934px 660px',
    label: 'Loading your schedule…',
  },
  invoices: {
    crop: '1118 138 455 562',
    part: 'M1450 460L1490 382L1573 399L1531 469Z',
    pivot: '1497px 460px',
    label: 'Loading invoices…',
  },
} satisfies Record<
  'welcome' | 'shifts' | 'invoices',
  { crop: string; part: string; pivot: string; label: string }
>;

/** Presentational only: the caller owns loading, errors and completion. */
export function WorkmateLoader({
  scene = 'juggling',
  label,
  paused = false,
  compact = false,
}: {
  scene?: WorkmateScene;
  label?: string;
  paused?: boolean;
  compact?: boolean;
}) {
  const id = useId().replace(/:/g, '');
  const [artFailed, setArtFailed] = useState(false);
  const taskScene =
    scene === 'juggling' || scene === 'team' || scene === 'approvals' ? scene : null;
  const config =
    scenes[scene === 'welcome' || scene === 'shifts' || scene === 'invoices' ? scene : 'welcome'];
  const defaultLabel = taskScene
    ? { juggling: 'Loading page…', team: 'Loading your team…', approvals: 'Loading approvals…' }[
        taskScene
      ]
    : config.label;
  const art = (
    <image
      href="/assets/mascot/workmate-transparent-v2.png"
      width="1604"
      height="980"
      onError={() => setArtFailed(true)}
    />
  );

  return (
    <div
      className={cn(
        'workmate-loader',
        paused && 'workmate-loader--paused',
        compact && 'workmate-loader--compact',
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {!artFailed && (
        <div className="workmate-loader__art" aria-hidden="true">
          <>
            {taskScene ? (
              <WorkmateTaskArt scene={taskScene} onError={() => setArtFailed(true)} />
            ) : (
              <svg viewBox={config.crop} focusable="false">
                <defs>
                  <mask
                    id={`${id}-base`}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="1604"
                    height="980"
                  >
                    <rect width="1604" height="980" fill="white" />
                    <path d={config.part} fill="black" />
                  </mask>
                  <clipPath id={`${id}-part`}>
                    <path d={config.part} />
                  </clipPath>
                </defs>
                <g className="workmate-loader__breath">
                  <g mask={`url(#${id}-base)`}>{art}</g>
                  <g
                    className={`workmate-loader__${scene}`}
                    style={{ transformOrigin: config.pivot }}
                  >
                    <g clipPath={`url(#${id}-part)`}>{art}</g>
                  </g>
                </g>
              </svg>
            )}
          </>
        </div>
      )}
      <span className="workmate-loader__activity" aria-hidden="true">
        <span />
      </span>
      <p className="workmate-loader__label">{label ?? defaultLabel}</p>
    </div>
  );
}
